import { Injectable, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MODULOS, Modulo, pantallasDe } from 'src/app/presentation/layouts/full/vertical/sidebar/modulos';
import { NavItem } from 'src/app/presentation/layouts/full/vertical/sidebar/nav-item/nav-item';

export interface PantallaBuscable extends NavItem {
  modulo: string;
  grupo?: string;
  color: string;
}

/**
 * Módulo activo del ERP. Se deduce del prefijo de la URL; en rutas que no son de ningún módulo
 * (perfil, páginas de la plantilla) conserva el último usado, recordado en el navegador.
 */
@Injectable({ providedIn: 'root' })
export class ModuloService {
  private readonly KEY = 'erp_modulo';

  readonly modulos = MODULOS;
  readonly modulo = signal<Modulo>(this.recordado());
  readonly navItems = computed(() => this.modulo().items);

  /** Todas las pantallas de todos los módulos, para el buscador del header. */
  readonly todasLasPantallas: PantallaBuscable[] = MODULOS.flatMap((m) =>
    pantallasDe(m).map((p) => ({ ...p, modulo: m.nombre, color: m.color }))
  );

  constructor(private router: Router) {
    this.actualizar(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.actualizar(e.urlAfterRedirects));
  }

  /** Número de pantallas de un módulo (para el lanzador). */
  pantallas(m: Modulo): number {
    return pantallasDe(m).length;
  }

  private actualizar(url: string): void {
    const ruta = (url || '').split(/[?#]/)[0];
    const m = MODULOS.find((mod) => mod.prefijos.some((p) => ruta === p || ruta.startsWith(p + '/')));
    if (m && m.id !== this.modulo().id) {
      this.modulo.set(m);
      try {
        localStorage.setItem(this.KEY, m.id);
      } catch {}
    }
  }

  private recordado(): Modulo {
    let id: string | null = null;
    try {
      id = localStorage.getItem(this.KEY);
    } catch {}
    return MODULOS.find((m) => m.id === id) ?? MODULOS[0];
  }
}
